import styled from "styled-components";

/**
 * Room around the pill for what it paints outside itself: the active segment's `0 2px 9px` shadow
 * (9px blur, pushed 2px down) and the 2px focus outline 2px out. Sides and top take the blur; the
 * bottom takes the blur plus the offset.
 */
const RAIL_ROOM_X = 10;
const RAIL_ROOM_TOP = 8;
const RAIL_ROOM_BOTTOM = 12;

/**
 * Externalized styles for {@link ./SegmentedTabs} (ui#163) — the house segmented tab pill, carried
 * over 1:1 from the pricing `.billtog` idiom ({@link ../pricing/BillingToggle}): a `field`-coloured
 * rail whose active option lifts onto a raised `card` surface with a soft brand-blue shadow. Every
 * colour reads `theme.tokens`, so light + dark fall out of the token layer for free.
 */

/**
 * The one scroll container for every tab rail: when the segments are wider than the space they have,
 * this scrolls sideways instead of pushing its parent wider. Five admin tabs and the brand sheet's
 * three are both wider than a 375px phone (kitecorp/transferify-ui#3013, ui#648), and an overflowing
 * rail had scrolled the whole admin panel off the left edge.
 *
 * It sits OUTSIDE the pill rather than making the pill scroll, because a scroll container clips what
 * it paints: the active segment's shadow and the keyboard focus ring reach past the pill's own 4px
 * padding. The padding here is that room, and the matching negative margin keeps the rail exactly
 * where it was on the page. A rail that fits is unchanged.
 */
export const Rail = styled.div`
	display: inline-flex;
	max-width: calc(100% + ${RAIL_ROOM_X * 2}px);
	margin: -${RAIL_ROOM_TOP}px -${RAIL_ROOM_X}px -${RAIL_ROOM_BOTTOM}px;
	padding: ${RAIL_ROOM_TOP}px ${RAIL_ROOM_X}px ${RAIL_ROOM_BOTTOM}px;
	overflow-x: auto;
	overflow-y: hidden;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

/** The pill rail (design `.billtog`): the field-coloured track the segments sit inside. */
export const Track = styled.div`
	display: inline-flex;
	flex: none;
	background: ${({theme}) => theme.tokens.field};
	border-radius: 13px;
	padding: 4px;
	gap: 3px;
`;

/** A single segment (design `.billtog button`): active = raised `card` option, inactive = `ink2` ghost. */
export const Segment = styled.button<{$active: boolean}>`
	flex: none;
	white-space: nowrap;
	border: none;
	font-weight: 700;
	font-size: 13px;
	font-family: inherit;
	padding: 9px 20px;
	border-radius: 9px;
	cursor: pointer;
	transition: color 0.16s, background 0.16s, box-shadow 0.16s;
	color: ${({theme, $active}) => ($active ? theme.tokens.ink : theme.tokens.ink2)};
	background: ${({theme, $active}) => ($active ? theme.tokens.card : "transparent")};
	box-shadow: ${({$active}) => ($active ? "0 2px 9px rgba(15,39,133,.14)" : "none")};

	&:hover {
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;
